// HeroBannerSplit.js — Variante "Banner con logos" de la sección Hero.
//
// Diseño de dos columnas sobre una imagen de fondo (opcionalmente con efecto
// parallax) + una forma decorativa (SVG/PNG) sobre el fondo:
//
//   ┌───────────────────────────────────────────────────────────┐
//   │  [logo principal]                          [logo aliado]  │
//   │  Título con <strong>                                      │
//   │  [ Botón ]  [ Botón ]                                     │
//   └───────────────────────────────────────────────────────────┘
//
// Se activa desde el admin con `config.variante = 'banner_split'`. Cualquier
// otro valor (o ausencia) mantiene el Hero clásico intacto, así que ningún
// proyecto existente cambia.
//
// Todo (imágenes, tamaños por dispositivo, colores, posiciones, textos,
// acciones de los botones) es configurable desde HeroForm.
import React from 'react';
import styled, { css } from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { useIdioma } from '../../../contexts/IdiomaContext';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveDeviceValue } from '../../admin/DeviceTabsSelect';

const MQ_TABLET = '@media (max-width: 991px) and (min-width: 601px)';
const MQ_MOBILE = '@media (max-width: 600px)';
// Todo lo que no es escritorio (móvil + tablet) — el diseño original centra
// logo y textos a partir de 991px.
const MQ_COMPACT = '@media (max-width: 991px)';

const resolveUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_BASE}/${url}`;
};

// Convierte 'left'|'center'|'right' en el valor de justify-content correspondiente
const toJustify = (a) => (a === 'left' ? 'flex-start' : a === 'right' ? 'flex-end' : 'center');

const Section = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: ${p => p.$vAlign || 'center'};
  min-height: ${p => p.$minHeight || '600px'};
  ${p => p.$bgImage
    ? css`
      background-image: url(${p.$bgImage});
      background-size: ${p.$bgSize || 'cover'};
      background-position: ${p.$bgPos || 'center'};
      background-repeat: no-repeat;
      background-color: ${p.$bgColor || '#0f355b'};
      background-attachment: ${p.$parallax ? 'fixed' : 'scroll'};
    `
    : css`background: ${p.$bg || p.$bgColor || '#0f355b'};`}
  /* Altura máxima SOLO en escritorio: en pantallas chicas el contenido se apila
     y necesita crecer, así que el tope no aplica. */
  ${p => p.$maxHeight && `@media (min-width: 992px) { max-height: ${p.$maxHeight}; }`}
  ${MQ_COMPACT} {
    min-height: ${p => p.$minHeightCompact || p.$minHeight || '520px'};
    /* En móvil el parallax "fixed" se rompe en iOS → lo forzamos a scroll */
    background-attachment: scroll;
    background-position: ${p => p.$bgPosCompact || p.$bgPos || 'center'};
  }
`;

// Capa de color/degradado por encima de la imagen (overlay opcional)
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${p => p.$overlay};
`;

// Forma decorativa (el SVG azul curvo del diseño original)
const Forma = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background-image: url(${p => p.$url});
  background-repeat: no-repeat;
  background-size: ${p => p.$size || 'contain'};
  background-position: ${p => p.$pos || 'bottom left'};
  opacity: ${p => (p.$opacity != null ? p.$opacity : 1)};
  ${MQ_COMPACT} { ${p => p.$hideCompact && 'display: none;'} }
`;

const Row = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: ${p => p.$maxWidth || '1200px'};
  margin: 0 auto;
  padding: ${p => p.$padding || '3rem 2rem'};
  display: flex;
  align-items: flex-start;
  gap: ${p => p.$gap || '2rem'};
  ${MQ_COMPACT} {
    flex-direction: column;
    padding: ${p => p.$paddingCompact || '2.5rem 1.25rem'};
    gap: 1.25rem;
  }
`;

const ColTexto = styled.div`
  width: ${p => p.$width || '60%'};
  ${MQ_COMPACT} { width: 100%; }
`;

// Logo y título en la misma línea (con divisor opcional), como en el diseño
// de banner interno. En móvil siempre se apilan.
const FilaLogoTitulo = styled.div`
  display: flex;
  align-items: ${p => p.$align || 'center'};
  justify-content: ${p => p.$justify || 'center'};
  gap: ${p => p.$gap || '2.5rem'};
  flex-wrap: nowrap;
  ${MQ_COMPACT} {
    flex-direction: column;
    gap: ${p => p.$gapCompact || '1rem'};
    align-items: center;
  }
`;

/* Altura explícita a propósito: con `align-self: stretch` + `margin: auto 0` el
   navegador deja de estirar la barra (los márgenes automáticos mandan) y queda
   de 0px de alto, o sea invisible. */
const DivisorVert = styled.span`
  flex: 0 0 auto;
  align-self: center;
  display: block;
  width: ${p => p.$ancho || '2px'};
  height: ${p => p.$alto || '120px'};
  background: ${p => p.$color || '#ffffff'};
  ${MQ_COMPACT} { display: none; }
`;

const ColAliado = styled.div`
  width: ${p => p.$width || '40%'};
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  ${MQ_COMPACT} { width: 100%; justify-content: center; }
`;

const LogoWrap = styled.div`
  text-align: ${p => p.$align || 'left'};
  margin-top: ${p => p.$mt || '0'};
  margin-bottom: ${p => p.$mb || '0'};
  ${MQ_TABLET} {
    text-align: ${p => p.$alignTablet || 'center'};
    margin-top: ${p => p.$mtTablet || p.$mt || '0'};
    margin-bottom: ${p => p.$mbTablet || p.$mb || '0'};
  }
  ${MQ_MOBILE} {
    text-align: ${p => p.$alignMobile || 'center'};
    margin-top: ${p => p.$mtMobile || p.$mt || '0'};
    margin-bottom: ${p => p.$mbMobile || p.$mb || '0'};
  }
  a { display: inline-block; }
`;

const LogoImg = styled.img`
  display: inline-block;
  width: ${p => p.$w || '400px'};
  max-width: 100%;
  height: auto;
  ${MQ_TABLET} { width: ${p => p.$wTablet || p.$w || '300px'}; }
  ${MQ_MOBILE} { width: ${p => p.$wMobile || p.$wTablet || p.$w || '300px'}; }
`;

const Titulo = styled.h1`
  margin: ${p => p.$margin || '0 0 1.5rem'};
  color: ${p => p.$color || '#ffffff'};
  font-family: ${p => p.$font || 'inherit'};
  font-size: ${p => p.$size || '36px'};
  font-weight: ${p => p.$weight || '400'};
  line-height: ${p => p.$lineHeight || '1.2'};
  text-align: ${p => p.$align || 'left'};
  ${p => p.$shadow && `text-shadow: ${p.$shadow};`}
  strong, b {
    font-size: ${p => p.$strongSize || '48px'};
    font-weight: ${p => p.$strongWeight || '700'};
    line-height: 1;
    display: ${p => (p.$strongBlock ? 'block' : 'inline')};
  }
  ${MQ_TABLET} {
    font-size: ${p => p.$sizeTablet || p.$size || '30px'};
    text-align: ${p => p.$alignTablet || 'center'};
    strong, b { font-size: ${p => p.$strongSizeTablet || p.$strongSize || '38px'}; }
  }
  ${MQ_MOBILE} {
    font-size: ${p => p.$sizeMobile || p.$sizeTablet || p.$size || '20px'};
    text-align: ${p => p.$alignMobile || p.$alignTablet || 'center'};
    strong, b { font-size: ${p => p.$strongSizeMobile || p.$strongSizeTablet || p.$strongSize || '26px'}; }
    ${p => p.$shadowMobile && `text-shadow: ${p.$shadowMobile};`}
  }
`;

const Subtitulo = styled.div`
  color: ${p => p.$color || '#ffffff'};
  font-family: ${p => p.$font || 'inherit'};
  font-size: ${p => p.$size || '18px'};
  font-weight: ${p => p.$weight || '400'};
  line-height: ${p => p.$lineHeight || '1.5'};
  text-align: ${p => p.$align || 'left'};
  margin: ${p => p.$margin || '0 0 1.5rem'};
  ${MQ_COMPACT} { text-align: ${p => p.$alignCompact || 'center'}; }
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.$gap || '28px'};
  justify-content: ${p => p.$justify || 'flex-start'};
  margin-top: ${p => p.$mt || '0'};
  ${MQ_COMPACT} { justify-content: ${p => p.$justifyCompact || 'center'}; }
`;

const Btn = styled.button`
  display: inline-block;
  background: ${p => p.$bg || 'transparent'};
  color: ${p => p.$color || '#ffffff'};
  border: ${p => p.$border || '1px solid #ffffff'};
  border-radius: ${p => p.$radius || '0px'};
  padding: ${p => p.$padding || '11px 21px 10px'};
  font-family: inherit;
  font-size: ${p => p.$size || '18px'};
  font-weight: ${p => p.$weight || 'bold'};
  line-height: 1;
  text-align: center;
  text-transform: ${p => p.$transform || 'none'};
  cursor: pointer;
  transition: 0.5s ease all;
  ${p => p.$shadow && 'box-shadow: 5px 7px 17px rgba(0, 0, 0, 0.15);'}
  &:hover {
    ${p => p.$hoverColor && css`
      color: ${p.$hoverColor};
      border-color: ${p.$hoverColor};
    `}
    ${p => p.$hoverBg && `background: ${p.$hoverBg};`}
  }
  ${MQ_MOBILE} { font-size: ${p => p.$sizeMobile || p.$size || '16px'}; }
`;

// Logo del aliado/patrocinador — puede ir anclado a la esquina de la sección
// (como en el diseño original) o fluir dentro de la columna derecha.
const AliadoAbs = styled.div`
  position: absolute;
  z-index: 4;
  top: ${p => p.$top || '12px'};
  right: ${p => p.$right || '7px'};
  ${MQ_COMPACT} {
    ${p => p.$centrarCompact
      ? 'left: 0; right: 0; text-align: center;'
      : `right: ${p.$rightCompact || p.$right || '12px'};`}
    top: ${p => p.$topCompact || p.$top || '12px'};
  }
`;

const AliadoImg = styled.img`
  display: inline-block;
  width: ${p => p.$w || '124px'};
  height: ${p => p.$h || 'auto'};
  max-width: 100%;
  object-fit: contain;
  ${MQ_COMPACT} {
    width: ${p => p.$wCompact || p.$w || '110px'};
    height: ${p => p.$hCompact || p.$h || 'auto'};
  }
`;

const HeroBannerSplit = ({ config, proyecto, openLogin, openRegister, tl }) => {
  const c = config || {};
  const { t } = useIdioma();
  const { user } = useAuth();

  const primary = c.color_primario || proyecto?.color_primario || '#0f355b';
  const secondary = c.color_secundario || proyecto?.color_secundario || '#14b6cb';

  const bgImg = resolveUrl(c.imagen_fondo);
  const logoUrl = resolveUrl(c.banner_logo);
  const formaUrl = resolveUrl(c.banner_forma);
  const aliadoUrl = resolveUrl(c.banner_logo_aliado);

  // ---- Tamaños / alineaciones por dispositivo -------------------------------
  const logoW = resolveDeviceValue(c.banner_logo_ancho, '');
  const logoMt = resolveDeviceValue(c.banner_logo_margen_superior, '');
  const logoMb = resolveDeviceValue(c.banner_logo_margen_inferior, '');
  const logoAlign = resolveDeviceValue(c.banner_logo_alineacion, '');
  const tSize = resolveDeviceValue(c.banner_titulo_tamano, '');
  const tStrong = resolveDeviceValue(c.banner_titulo_strong_tamano, '');
  const tAlign = resolveDeviceValue(c.banner_titulo_alineacion, '');
  const aliadoW = resolveDeviceValue(c.banner_logo_aliado_ancho, '');
  const aliadoH = resolveDeviceValue(c.banner_logo_aliado_alto, '');

  // Alineación de los botones: por defecto sigue la del título
  const btnAlign = c.banner_botones_alineacion || tAlign.desktop || 'left';
  const btnAlignCompact = c.banner_botones_alineacion_movil
    || (tAlign.mobile && tAlign.mobile !== tAlign.desktop ? tAlign.mobile : 'center');

  // ---- Botones (reusan la config del Hero clásico) --------------------------
  const accionLogin = c.accion_boton_login || 'login';
  const accionRegistro = c.accion_boton_registro || 'registro';
  // Si ya inició sesión, los botones que abren el modal de auth no tienen sentido
  const mostrarLogin = (c.boton_login !== false) && !(user && accionLogin === 'login');
  const mostrarRegistro = (c.boton_registro !== false) && !(user && accionRegistro === 'registro');

  const textoLogin = tl(c.texto_boton_login, '') || t('hero_btn_login');
  const textoRegistro = tl(c.texto_boton_registro, '') || t('hero_btn_registro');

  const handleAction = (action) => {
    const opts = { showLoginTab: c.boton_login !== false, showRegisterTab: c.boton_registro !== false };
    if (action === 'login') openLogin?.(opts);
    else if (action === 'registro') openRegister?.(opts);
    else if (action && action.startsWith('http')) window.open(action, '_blank', 'noopener');
    else if (action) window.location.href = action;
  };

  const hoverColor = c.banner_boton_hover_color || secondary;
  const conSombraBtn = c.banner_botones_sombra !== false;

  const anchoTexto = c.banner_ancho_columna || '60%';
  const anchoAliado = `${100 - (parseFloat(anchoTexto) || 60)}%`;
  const aliadoAbsoluto = c.banner_logo_aliado_posicion !== 'columna';

  const logoNode = logoUrl && (
    <LogoWrap
      $align={logoAlign.desktop || 'left'}
      $alignTablet={logoAlign.tablet || 'center'}
      $alignMobile={logoAlign.mobile || 'center'}
      $mt={logoMt.desktop || '120px'}
      $mtTablet={logoMt.tablet}
      $mtMobile={logoMt.mobile}
      $mb={logoMb.desktop || '2rem'}
      $mbTablet={logoMb.tablet}
      $mbMobile={logoMb.mobile}
    >
      {c.banner_logo_enlace ? (
        <a href={c.banner_logo_enlace}>
          <LogoImg
            src={logoUrl}
            alt={c.banner_logo_alt || proyecto?.nombre || 'Logo'}
            $w={logoW.desktop || '400px'}
            $wTablet={logoW.tablet}
            $wMobile={logoW.mobile}
          />
        </a>
      ) : (
        <LogoImg
          src={logoUrl}
          alt={c.banner_logo_alt || proyecto?.nombre || 'Logo'}
          $w={logoW.desktop || '400px'}
          $wTablet={logoW.tablet}
          $wMobile={logoW.mobile}
        />
      )}
    </LogoWrap>
  );

  // Título del banner. Se declara aparte porque puede ir apilado bajo el logo
  // o en la misma línea que él (banner_logo_en_linea).
  const tituloNode = (
    <Titulo
      $color={c.color_titulo || '#ffffff'}
      $font={c.fuente_titulo}
      $size={tSize.desktop || '36px'}
      $sizeTablet={tSize.tablet || '30px'}
      $sizeMobile={tSize.mobile || '20px'}
      $weight={c.peso_titulo || '400'}
      $lineHeight={c.interlineado_titulo || '1.2'}
      $strongSize={tStrong.desktop || '48px'}
      $strongSizeTablet={tStrong.tablet || '38px'}
      $strongSizeMobile={tStrong.mobile || '26px'}
      $strongWeight={c.banner_titulo_strong_peso || '700'}
      $strongBlock={c.banner_titulo_strong_bloque === true}
      $align={tAlign.desktop || 'left'}
      $alignTablet={tAlign.tablet || 'center'}
      $alignMobile={tAlign.mobile || 'center'}
      $margin={c.banner_titulo_margen || '0 0 1.5rem'}
      $shadow={c.banner_titulo_sombra}
      $shadowMobile={c.banner_titulo_sombra_movil !== false
        ? (c.banner_titulo_sombra || '3px 3px 4px rgba(0,0,0,0.97)')
        : null}
      dangerouslySetInnerHTML={{ __html: tl(c.titulo, '') }}
    />
  );

  const enLinea = c.banner_logo_en_linea === true && !!logoUrl;

  const aliadoImgNode = aliadoUrl && (
    <AliadoImg
      src={aliadoUrl}
      alt={c.banner_logo_aliado_alt || 'Logo aliado'}
      $w={aliadoW.desktop || '124px'}
      $wCompact={aliadoW.mobile || aliadoW.tablet}
      $h={aliadoH.desktop || 'auto'}
      $hCompact={aliadoH.mobile || aliadoH.tablet}
    />
  );

  const aliadoConEnlace = aliadoUrl && (
    c.banner_logo_aliado_enlace
      ? <a href={c.banner_logo_aliado_enlace} target="_blank" rel="noopener noreferrer">{aliadoImgNode}</a>
      : aliadoImgNode
  );

  return (
    <Section
      $bg={buildBg(c, primary)}
      $bgColor={primary}
      $bgImage={bgImg}
      $bgSize={c.tamano_fondo || 'cover'}
      $bgPos={c.posicion_fondo || 'center'}
      $bgPosCompact={c.banner_posicion_fondo_movil}
      $parallax={c.banner_parallax === true}
      $vAlign={c.alineacion_vertical || 'center'}
      $minHeight={c.pantalla_completa ? '100vh' : (c.altura_minima || '600px')}
      $minHeightCompact={c.banner_altura_minima_movil}
      $maxHeight={c.pantalla_completa ? null : c.banner_altura_maxima}
    >
      {c.overlay_fondo && <Overlay $overlay={c.overlay_fondo} />}
      {formaUrl && (
        <Forma
          $url={formaUrl}
          $size={c.banner_forma_tamano || 'contain'}
          $pos={c.banner_forma_posicion || 'bottom left'}
          $opacity={c.banner_forma_opacidad != null ? parseFloat(c.banner_forma_opacidad) : 1}
          $hideCompact={c.banner_forma_ocultar_movil !== false}
        />
      )}

      {aliadoAbsoluto && aliadoUrl && (
        <AliadoAbs
          $top={c.banner_logo_aliado_top || '12px'}
          $right={c.banner_logo_aliado_right || '7px'}
          $topCompact={c.banner_logo_aliado_top_movil}
          $rightCompact={c.banner_logo_aliado_right_movil}
          $centrarCompact={c.banner_logo_aliado_centrar_movil === true}
        >
          {aliadoConEnlace}
        </AliadoAbs>
      )}

      <Row
        $maxWidth={c.ancho_maximo || '1200px'}
        $padding={c.padding || '3rem 2rem'}
        $paddingCompact={c.banner_padding_movil}
        $gap={c.banner_gap_columnas}
      >
        <ColTexto $width={aliadoAbsoluto ? '100%' : anchoTexto}>
          {!enLinea && logoNode}
          {enLinea && (
            <FilaLogoTitulo
              $justify={toJustify(c.banner_logo_en_linea_alineacion || 'center')}
              $align={c.banner_logo_en_linea_vertical || 'center'}
              $gap={c.banner_logo_en_linea_gap}
              $gapCompact={c.banner_logo_en_linea_gap_movil}
            >
              {logoNode}
              {c.banner_divisor === true && (
                <DivisorVert
                  $color={c.banner_divisor_color}
                  $ancho={c.banner_divisor_ancho}
                  $alto={c.banner_divisor_alto}
                />
              )}
              <div style={{ minWidth: 0 }}>{tituloNode}</div>
            </FilaLogoTitulo>
          )}
          {!enLinea && tituloNode}
          {tl(c.subtitulo, '') && (
            <Subtitulo
              $color={c.color_subtitulo || '#ffffff'}
              $font={c.fuente_subtitulo}
              $size={c.tamano_subtitulo || '18px'}
              $weight={c.peso_subtitulo || '400'}
              $lineHeight={c.interlineado_subtitulo || '1.5'}
              $align={tAlign.desktop || 'left'}
              $alignCompact={tAlign.mobile || 'center'}
              $margin={c.margen_subtitulo}
              dangerouslySetInnerHTML={{ __html: tl(c.subtitulo, '') }}
            />
          )}
          {(mostrarLogin || mostrarRegistro) && (
            <BtnRow
              $justify={toJustify(btnAlign)}
              $justifyCompact={toJustify(btnAlignCompact)}
              $gap={c.banner_botones_gap || '28px'}
              $mt={c.margen_botones}
            >
              {mostrarLogin && (
                <Btn
                  type="button"
                  $bg={c.color_boton_login || 'transparent'}
                  $color={c.color_texto_boton_login || '#ffffff'}
                  $border={c.borde_boton_login || '1px solid #ffffff'}
                  $radius={c.radio_boton_login || '0px'}
                  $padding={c.padding_boton_login || '11px 21px 10px'}
                  $size={c.banner_boton_tamano || '18px'}
                  $sizeMobile={c.banner_boton_tamano_movil}
                  $transform={c.transform_boton_login || 'none'}
                  $shadow={conSombraBtn}
                  $hoverColor={hoverColor}
                  $hoverBg={c.banner_boton_hover_fondo}
                  onClick={() => handleAction(accionLogin)}
                >
                  {textoLogin}
                </Btn>
              )}
              {mostrarRegistro && (
                <Btn
                  type="button"
                  $bg={c.color_boton_registro || 'transparent'}
                  $color={c.color_texto_boton_registro || '#ffffff'}
                  $border={c.borde_boton_registro || '1px solid #ffffff'}
                  $radius={c.radio_boton_registro || '0px'}
                  $padding={c.padding_boton_registro || '11px 21px 10px'}
                  $size={c.banner_boton_tamano || '18px'}
                  $sizeMobile={c.banner_boton_tamano_movil}
                  $transform={c.transform_boton_registro || 'none'}
                  $shadow={conSombraBtn}
                  $hoverColor={hoverColor}
                  $hoverBg={c.banner_boton_hover_fondo}
                  onClick={() => handleAction(accionRegistro)}
                >
                  {textoRegistro}
                </Btn>
              )}
            </BtnRow>
          )}
        </ColTexto>

        {!aliadoAbsoluto && aliadoUrl && (
          <ColAliado $width={anchoAliado}>{aliadoConEnlace}</ColAliado>
        )}
      </Row>
    </Section>
  );
};

export default HeroBannerSplit;
